import React, { useState } from 'react';
import api from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { STATUS_LABELS, STATUS_COLORS } from '../lib/constants';
import PackageStatusBadge from './PackageStatusBadge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { CountryRoute } from './CountryDisplay';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Package, User, MapPin, Ship, X, Loader2, Camera } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';

const PackageSearch = ({ open, onOpenChange }) => {
  const { t, language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const statusLabels = (STATUS_LABELS[language] || STATUS_LABELS.fr);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();

    const hasText = searchQuery.trim().length >= 2;
    const hasFilters = statusFilter || dateFrom || dateTo;

    if (!hasText && !hasFilters) return;

    setLoading(true);
    setSearched(true);

    try {
      const params = { search_type: searchType };
      if (searchQuery.trim()) params.query = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + 'T23:59:59').toISOString();

      const response = await api.get('/api/packages/search', { params });
      setResults(response.data.results || []);
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setResults([]);
    setSearched(false);
  };

  const handleClose = () => {
    clearSearch();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!max-w-[calc(100vw-1rem)] sm:!max-w-4xl max-h-[85vh] overflow-y-auto overflow-x-hidden rounded-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Search className="h-6 w-6 text-accent" />
            {t('searchPackage')}
          </DialogTitle>
        </DialogHeader>

        {/* Formulaire de recherche */}
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1">
              <Input
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="text-base sm:text-lg"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('allFields')}</SelectItem>
                  <SelectItem value="oce_code">{t('oceCodeField')}</SelectItem>
                  <SelectItem value="sender">{t('senderField')}</SelectItem>
                  <SelectItem value="recipient">{t('recipientField')}</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" className="shrink-0" disabled={loading || (searchQuery.trim().length < 2 && !statusFilter && !dateFrom && !dateTo)}>
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="hidden sm:inline ml-1">{t('searchButton')}</span>
              </Button>
              {(searchQuery || statusFilter || dateFrom || dateTo) && (
                <Button type="button" variant="outline" className="shrink-0" onClick={clearSearch}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Filtres avancés */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">{t('statusLabel')}</Label>
              <Select value={statusFilter || "all_statuses"} onValueChange={(v) => setStatusFilter(v === 'all_statuses' ? '' : v)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('allStatuses')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_statuses">{t('allStatuses')}</SelectItem>
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">{t('startDate')}</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">{t('endDate')}</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </form>

        {/* Résultats */}
        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : searched ? (
            results.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-lg">{t('noPackageFound')} "{searchQuery}"</p>
                <p className="text-sm mt-2">{t('tryOtherSearch')}</p>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  {results.length} {t('resultsFound')}
                </p>
                <div className="space-y-3">
                  {results.map((result, idx) => {
                    const pkg = result.package;
                    return (
                      <Card key={idx} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex gap-3 min-w-0">
                            {/* Photo thumbnail */}
                            {pkg.photo_url ? (
                              <div className="shrink-0 w-14 h-14 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-muted">
                                <img
                                  src={pkg.photo_url}
                                  alt={pkg.oce_code}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                />
                                <div className="w-full h-full items-center justify-center hidden">
                                  <Camera className="h-5 w-5 text-muted-foreground" />
                                </div>
                              </div>
                            ) : pkg.photo_count > 0 ? (
                              <div className="shrink-0 w-14 h-14 sm:w-20 sm:h-20 rounded-lg bg-muted flex items-center justify-center">
                                <Camera className="h-5 w-5 text-muted-foreground" />
                              </div>
                            ) : null}

                            <div className="min-w-0 flex-1">
                              {/* Code OCE */}
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Package className="h-4 w-4 sm:h-5 sm:w-5 text-accent shrink-0" />
                                <span className="font-bold text-base sm:text-lg">{pkg.oce_code}</span>
                                <PackageStatusBadge status={pkg.status} size="sm" />
                              </div>

                              {/* Expéditeur -> Destinataire */}
                              <div className="flex flex-wrap items-center gap-1 sm:gap-4 text-sm text-muted-foreground mb-2">
                                <div className="flex items-center gap-1 min-w-0">
                                  <User className="h-3.5 w-3.5 shrink-0" />
                                  <span className="truncate max-w-[120px] sm:max-w-none">{pkg.sender_name}</span>
                                </div>
                                <span>→</span>
                                <div className="flex items-center gap-1 min-w-0">
                                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                                  <span className="truncate max-w-[120px] sm:max-w-none">{pkg.recipient_name}</span>
                                </div>
                              </div>

                              {/* Détails supplémentaires */}
                              <div className="flex flex-wrap items-center gap-1 sm:gap-3 text-xs text-muted-foreground">
                                <span>{t('weightLabel')}: {pkg.weight} kg</span>
                                {pkg.description && <span className="truncate max-w-[150px] sm:max-w-none">• {pkg.description}</span>}
                                <span>• {new Date(pkg.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                              </div>

                              {/* Départ */}
                              {result.shipment && (
                                <div className="mt-3 p-2 bg-accent/10 rounded-lg">
                                  <div className="flex items-center gap-2 text-sm min-w-0 flex-wrap">
                                    <Ship className="h-4 w-4 text-accent shrink-0" />
                                    <span className="font-medium text-accent truncate min-w-0">
                                      {t('shipmentDeparture')}: {result.shipment.name}
                                    </span>
                                    <Badge variant="outline" className="text-xs shrink-0">
                                      {result.shipment.status}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-accent mt-1 ml-6">
                                    <CountryRoute origin={result.shipment.origin_country} destination={result.shipment.destination_country} size="sm" />
                                  </p>
                                </div>
                              )}

                              {/* Partenaire */}
                              {result.partner && (
                                <div className="mt-2 text-xs text-gray-500">
                                  {t('collectedBy')}: <span className="font-medium">{result.partner.name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )
          ) : (
            <div className="text-center py-12 text-gray-400">
              <Search className="h-12 w-12 mx-auto mb-3" />
              <p>{t('enterSearchTerm')}</p>
              <p className="text-sm mt-2">{t('minCharsSearch')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PackageSearch;
